// ============================================================
// Schreibsimulation der oeffentlichen Demo.
//
// Die Demo soll sich anfuehlen wie die Vollfassung — zeichnen, Sachdaten
// pflegen, Fotos laden, Maengel erfassen — und dabei kein Byte in PostgreSQL
// schreiben. Der billige Weg waere gewesen, die Knoepfe auszublenden; der
// ehrliche ist dieser: die Schreibaufrufe werden abgefangen, BEVOR ein Request
// entsteht, mit einer glaubwuerdigen Antwort beschieden und im Arbeitsspeicher
// gemerkt. Jede spaetere Leseantwort des Servers wird mit dem Gemerkten
// ueberlagert — deshalb duerfen die Bauteile ihre Abfragen ganz normal
// nachladen, ohne dass die Aenderung wieder verschwindet.
//
// Warum Modulzustand und ausdruecklich KEIN localStorage/sessionStorage und
// kein Service Worker: „ein Neuladen stellt den Ausgangsstand her" ist das
// Versprechen, mit dem diese Demo oeffentlich stehen kann. Was ein Neuladen
// ueberlebt, muesste jemand aufraeumen — und ein Service Worker faellt ohnehin
// unter den Fassungswaechter.
//
// Diese Datei ist die EINZIGE Stelle, die die Demo kennt. Angeschlossen ist sie
// an genau zwei Punkten in api/http.ts (req und postForm); kein Bildschirm und
// keine Komponente unterscheidet mehr zwischen Demo und Vollfassung.
// ============================================================
import type { ApiAttachment, ApiGeraet, ApiMangel, ApiPruefPunkt, ApiPruefung, HinweisCollection, HinweisGrund } from "../api/http";
import { artName } from "./geraete";
import { FRISTEN, fristVon, zuBehebenBis, fristUeberschritten } from "./frist";
import { useStore } from "../store/useStore";

/**
 * Ergebnis einer abgefangenen Anfrage. Bewusst ein Beschreibungsobjekt und kein
 * fertiges Promise: `ApiError` lebt in http.ts, und ein Wert-Import von dort
 * waere ein Ringschluss (http.ts importiert seinerseits diese Datei). Die
 * Absage wird deshalb nur BENANNT — geworfen wird sie drueben, wo die
 * Fehlerklasse zu Hause ist.
 */
export type DemoErgebnis<T> = { art: "antwort"; wert: T } | { art: "absage"; text: string };

/** Geometrie so schlank wie der createFeature-Vertrag sie ohnehin fuehrt. */
export interface DemoGeometrie {
  type: string;
  coordinates: unknown;
}

/** Was `demoGeometrien()` der Karte hinlegt — GeoJSON, aber ohne Abhaengigkeit
 *  auf @types/geojson (die tsconfig laesst nur `vite/client` automatisch zu). */
export interface DemoSammlung {
  type: "FeatureCollection";
  features: Array<{ type: "Feature"; id: string; geometry: DemoGeometrie; properties: Record<string, unknown> }>;
}

interface DemoObjekt {
  layerId: string;
  featureId: string;
  geometry: DemoGeometrie;
  values: Record<string, unknown>;
}

/** Eine Zeile, wie sie `POST /layers/:id/query/list` liefert. */
interface Objektzeile {
  fid: string;
  name: string | null;
  nr: string | null;
  district: string | null;
  bbox: [number, number, number, number];
  values?: Record<string, unknown>;
  total?: number;
}

/** Das Praefix macht jede erfundene Kennung auf einen Blick erkennbar — auch im
 *  Bildschirmfoto eines Fehlerberichts. Kollisionsfrei, weil echte Kennungen im
 *  ganzen Bestand entweder rein numerisch (gid) oder UUID sind. */
const PRAEFIX = "demo-";

const S = {
  /** Demo-Modus. Erste Abfrage in jeder Einhaengung — im Normalbetrieb kostet
   *  die ganze Schicht genau diesen einen Vergleich. */
  an: false,
  zaehler: 0,
  /** Schluessel ueberall `${layerId}/${featureId}`. */
  objekte: new Map<string, DemoObjekt>(),
  werte: new Map<string, Record<string, unknown>>(),
  maengel: new Map<string, ApiMangel[]>(),
  /** Selbst angelegte Spielgeraete je Flaeche (Baustein Geraeteverwaltung) */
  geraete: new Map<string, ApiGeraet[]>(),
  /** Pruefprotokolle dieser Sitzung je Flaeche (Baustein Spielplatzpruefung) */
  pruefungen: new Map<string, ApiPruefung[]>(),
  /** Erinnerungseinstellung je Projekt */
  erinnerung: new Map<string, { aktiv: boolean; rhythmus: "taeglich" | "woechentlich" }>(),
  /** Kategorienfarben der Diagramme je Projekt (Issue #127) */
  farben: new Map<string, Record<string, string>>(),
  anhaenge: new Map<string, ApiAttachment[]>(),
  /** Geloeschtes — auch ECHTE Kennungen. Ohne diese Menge kaeme ein geloeschtes
   *  Foto beim naechsten GET vom Server unweigerlich zurueck. */
  weg: new Set<string>(),
  /** Aenderungen an ECHTEN Zeilen (Stern, Mangel bearbeiten). Gleicher Grund. */
  anhangPatch: new Map<string, Partial<ApiAttachment>>(),
  mangelPatch: new Map<string, Partial<ApiMangel>>(),
  geraetPatch: new Map<string, Partial<ApiGeraet>>(),
  /** Anhang-Kennung -> Object-URL der Datei im Browser. */
  bilder: new Map<string, string>(),
  /** Kontoangaben, die die Besucherin in dieser Sitzung geaendert hat (Name,
   *  Profilbild, Sprache). Das Bild kommt als data:-URL aus dem Dateiwaehler,
   *  liegt also ohnehin schon im Browser — es braucht keinen Object-URL und
   *  keinen Server. Neuladen setzt alles zurueck, wie ueberall sonst. */
  konto: {} as Record<string, unknown>,
  /** Zuletzt vom Server gesehene Kontozeile — Vorlage fuer die Antwort auf
   *  einen simulierten Kontowechsel. */
  gesehenKonto: null as Record<string, unknown> | null,
  /** Zuletzt vom Server gesehene echte Zeilen. Nur dafuer da, dass ein PATCH
   *  auf eine echte Zeile die VOLLE Zeile zurueckgeben kann, so wie es der
   *  Server taete — erfinden muessten wir sonst Felder, die wir nicht kennen. */
  gesehenAnhang: new Map<string, ApiAttachment>(),
  gesehenMangel: new Map<string, ApiMangel>(),
  gesehenGeraet: new Map<string, ApiGeraet>(),
  /** Zuletzt vom Server gesehene Hinweis-Eintraege, je `${layerId}/${featureId}`.
   *  Einziger Zweck: die GEOMETRIE. Wer in der Demo einen Mangel auf „behoben"
   *  setzt und danach wieder auf „offen", soll sein Ausrufezeichen zurueck-
   *  bekommen — dafuer muss die Lage des Zeichens bekannt bleiben, und die
   *  liefert ausschliesslich der Server. */
  gesehenHinweis: new Map<string, HinweisCollection["features"][number]>(),
  /** Selbst gelernt: welche Spalte den Zeilenkopf name/nr fuellt (siehe lerneKopffelder). */
  kopffeld: new Map<string, { name?: string; nr?: string }>(),
  abonnenten: new Set<() => void>(),
  /** Wortlaut der Rueckmeldung: das erste Mal ausfuehrlich, danach kurz. */
  ersteMeldung: true,
  zuletztGemeldet: 0,
};

const key = (layerId: string, featureId: string) => `${layerId}/${featureId}`;
const heute = () => new Date().toISOString().slice(0, 10);
const jetzt = () => new Date().toISOString();
/** demo-f1 (Objekt), demo-m1 (Mangel), demo-a1 (Anhang). Eine gemeinsame,
 *  fortlaufende Nummer reicht — der Bestand lebt nur in diesem Fenster. */
const naechsteKennung = (art: "f" | "m" | "a" | "g") => `${PRAEFIX}${art}${++S.zaehler}`;

// ------------------------------------------------------------
// Modus und Bestand
// ------------------------------------------------------------

/**
 * Demo-Modus setzen. Gerufen wird das ausschliesslich aus http.ts:
 * `demoLogin()`, `login()`, `logout()` und — wichtig — `me()`. Ohne `me()` waere
 * die Simulation nach jedem Neuladen tot: das Sitzungs-Cookie lebt weiter, die
 * App bootstrappt ueber /auth/me, und die Schreibversuche liefen ungebremst in
 * den 403 des Backend-Riegels.
 *
 * Der Bestand wird nur beim WECHSEL geleert, nicht bei jedem Setzen — `me()`
 * laeuft mehrfach, und ein Aufraeumen dort loeschte die halbe Sitzung.
 */
export function demoModusSetzen(an: boolean): void {
  if (an === S.an) return;
  S.an = an;
  demoZuruecksetzen();
}

export function demoAktiv(): boolean {
  return S.an;
}

/** Alles vergessen — und die Object-URLs freigeben, sonst haelt der Browser die
 *  Dateien bis zum Schliessen des Fensters im Speicher. */
export function demoZuruecksetzen(): void {
  for (const url of S.bilder.values()) URL.revokeObjectURL(url);
  S.bilder.clear();
  S.objekte.clear();
  S.werte.clear();
  S.maengel.clear();
  S.geraete.clear();
  S.pruefungen.clear();
  S.erinnerung.clear();
  S.farben.clear();
  S.geraetPatch.clear();
  S.gesehenGeraet.clear();
  S.anhaenge.clear();
  S.weg.clear();
  S.anhangPatch.clear();
  S.mangelPatch.clear();
  S.gesehenAnhang.clear();
  S.gesehenMangel.clear();
  S.kopffeld.clear();
  S.zaehler = 0;
  S.ersteMeldung = true;
  S.zuletztGemeldet = 0;
  signal();
}

/** Die Karte zeichnet erfundene Objekte selbst — der Server kennt sie nie, also
 *  bringt `bustTiles()` sie nicht auf die Kachel. Hier erfaehrt sie davon. */
export function demoAbonnieren(fn: () => void): () => void {
  S.abonnenten.add(fn);
  return () => S.abonnenten.delete(fn);
}

function signal(): void {
  for (const fn of S.abonnenten) fn();
}

export function demoIstDemoObjekt(fid: string): boolean {
  return fid.startsWith(PRAEFIX);
}

/**
 * Die in dieser Sitzung gezeichneten Objekte einer Ebene als GeoJSON.
 *
 * Die Karte braucht das, weil `bustTiles()` nur die MVT-Adresse erneuert — und
 * unter dieser Adresse antwortet der Server, der das erfundene Objekt nie
 * kennengelernt hat. Gezeichnet wird es deshalb aus einer eigenen Quelle
 * ueber den Kacheln.
 */
export function demoGeometrien(layerId: string): DemoSammlung {
  const features: DemoSammlung["features"] = [];
  for (const o of S.objekte.values()) {
    if (o.layerId !== layerId) continue;
    features.push({
      type: "Feature" as const,
      id: o.featureId,
      geometry: o.geometry,
      properties: { ...o.values, ...(S.werte.get(key(o.layerId, o.featureId)) ?? {}), fid: o.featureId, demo: true },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Bilddaten eines erfundenen Fotos. `photoUrl()` fragt hier zuerst — sonst
 *  ginge fuer ein Foto, das nur im Browser existiert, doch noch eine echte
 *  Anfrage raus (das `<img src>` laeuft an api/react-query vorbei). */
export function demoFotoUrl(id: string): string | null {
  return S.bilder.get(id) ?? null;
}

// ------------------------------------------------------------
// Rueckmeldung
// ------------------------------------------------------------

/**
 * „Gespeichert" allein waere gelogen, „nicht gespeichert" waere falsch — der
 * Halbsatz danach loest es auf. Beim ersten Mal ausfuehrlich, danach kurz.
 *
 * Die Bremse von vier Sekunden ist kein Schoenheitsfehler: `mgUpload` schickt je
 * Datei einen eigenen Aufruf und haengt danach noch einen Patch hinterher — ohne
 * sie stapelten sich fuer einen Zug von fuenf Fotos sechs gleiche Meldungen.
 */
function rueckmeldung(): void {
  const lang = useStore.getState().lang;
  const t = Date.now();
  if (!S.ersteMeldung && t - S.zuletztGemeldet < 4000) return;
  const text = S.ersteMeldung
    ? lang === "de"
      ? "Gespeichert — in der Demo nur in diesem Browser; ein Neuladen stellt den Ausgangsstand her"
      : "Saved — in the demo this stays in your browser; a reload restores the original state"
    : lang === "de"
      ? "Gespeichert — nur in dieser Demo-Sitzung"
      : "Saved — only in this demo session";
  S.ersteMeldung = false;
  S.zuletztGemeldet = t;
  useStore.getState().addToast({ type: "info", msg: text });
}

/** Wortlaut fuer alles, was die Demo bewusst nicht nachstellt (§ „nicht simuliert"). */
function absageText(): string {
  return useStore.getState().lang === "de"
    ? "Das ist in der Demo nicht enthalten — hier ändert sich nichts"
    : "That is not part of the demo — nothing changes here";
}

/**
 * Absage fuer alles, was die Demo nicht nachstellt. Der Toast wird hier
 * ausgeloest und nicht dem Aufrufer ueberlassen: die Bauteile wuerden daraus
 * einen roten „Speichern fehlgeschlagen"-Fehler machen, und fehlgeschlagen ist
 * hier nichts — es war nur nie vorgesehen. Der Aufruf scheitert trotzdem
 * (http.ts wirft die Absage als ApiError), damit nichts so tut, als haette es
 * funktioniert.
 */
function absage<T>(): DemoErgebnis<T> {
  const text = absageText();
  useStore.getState().addToast({ type: "info", msg: text });
  return { art: "absage", text };
}

// ------------------------------------------------------------
// Pfade
// ------------------------------------------------------------

const P = {
  featureNeu: /^\/layers\/([^/]+)\/features$/,
  importLauf: /^\/layers\/([^/]+)\/features\/import$/,
  feature: /^\/layers\/([^/]+)\/features\/([^/]+)$/,
  split: /^\/layers\/([^/]+)\/features\/([^/]+)\/split$/,
  revert: /^\/layers\/([^/]+)\/features\/([^/]+)\/revert$/,
  anhaenge: /^\/layers\/([^/]+)\/features\/([^/]+)\/attachments$/,
  maengel: /^\/layers\/([^/]+)\/features\/([^/]+)\/maengel$/,
  geraete: /^\/layers\/([^/]+)\/features\/([^/]+)\/geraete$/,
  geraet: /^\/geraete\/([^/]+)$/,
  pruefungen: /^\/layers\/([^/]+)\/features\/([^/]+)\/pruefungen$/,
  pruefung: /^\/pruefungen\/([^/]+)$/,
  pruefungAbschluss: /^\/pruefungen\/([^/]+)\/abschliessen$/,
  erinnerung: /^\/projects\/([^/]+)\/erinnerung$/,
  farben: /^\/projects\/([^/]+)\/category-colors$/,
  projekt: /^\/projects\/([^/]+)$/,
  verlauf: /^\/layers\/([^/]+)\/features\/([^/]+)\/history$/,
  schnitt: /^\/layers\/([^/]+)\/features\/([^/]+)\/intersects\/([^/]+)$/,
  anhang: /^\/attachments\/([^/]+)$/,
  mangel: /^\/maengel\/([^/]+)$/,
  queryList: /^\/layers\/([^/]+)\/query\/list$/,
  queryCount: /^\/layers\/([^/]+)\/query\/count$/,
  hinweise: /^\/projects\/([^/]+)\/hinweise$/,
} as const;

/**
 * Nicht-GET-Pfade, die nachweislich nur LESEN und deshalb als einzige den
 * Browser verlassen duerfen. Dieselbe Liste steht spiegelbildlich im
 * Backend-Riegel (main.ts) — dort ist sie der harte Boden, hier spart sie den
 * Umweg ueber einen 403. Kurz halten: jeder Eintrag ist eine Ausnahme.
 */
const LESENDE_POSTS: RegExp[] = [
  /^\/auth\/(demo|logout|login)$/,
  /^\/projects\/[^/]+\/aggregate$/,
  /^\/projects\/[^/]+\/maengel\/(stats|tabelle|liste)$/,
  /^\/projects\/[^/]+\/geraete\/tabelle$/,
  /^\/layers\/[^/]+\/query\/(count|list)$/,
  /^\/projects\/[^/]+\/routen\/(optimieren|planen)$/,
];

/**
 * Buchhaltung, die niemand sieht: das Anwenden eines gespeicherten Filters
 * zaehlt serverseitig eine Nutzung hoch. Ein Schreibvorgang, aber die Handlung
 * davor ist ein LESEN — wer einen Filter anklickt, darf dafuer nicht „nicht
 * enthalten" zu hoeren bekommen. Abgewiesen wird er trotzdem, nur eben
 * WORTLOS; der Aufrufer verschluckt den Fehler ohnehin ausdruecklich
 * (SavedFilters: `onError: () => undefined`, „Komfort, kein Teil des Ladens").
 */
const STILL_ABGEWIESEN: RegExp[] = [/^\/projects\/[^/]+\/filters\/[^/]+\/used$/];

function teile(re: RegExp, pfad: string): string[] | null {
  const m = re.exec(pfad);
  if (!m) return null;
  // decodeURIComponent, weil die Aufrufer die Objektkennung kodiert einsetzen.
  // Der Fangarm ist kein Zierrat: ein einzelnes Prozentzeichen im Pfad wuerfe
  // hier einen URIError — mitten in req(), also fuer eine ganz normale Anfrage.
  return m.slice(1).map((s) => {
    try {
      return decodeURIComponent(s ?? "");
    } catch {
      return s ?? "";
    }
  });
}

function ohneAbfrage(path: string): string {
  const i = path.indexOf("?");
  return i < 0 ? path : path.slice(0, i);
}

// ------------------------------------------------------------
// Abfangen: Schreibaufrufe (und Lesezugriffe auf erfundene Objekte)
// ------------------------------------------------------------

/**
 * Die eine Tuer fuer alles, was ueber `req()` laeuft. Rueckgabe `null` heisst
 * „durchlassen"; im Normalbetrieb ist das die erste Zeile und der ganze
 * Aufwand.
 */
export function demoAbfangen<T>(method: string, path: string, body?: unknown): DemoErgebnis<T> | null {
  if (!S.an) return null;
  const p = ohneAbfrage(path);
  const lesend = method === "GET" || method === "HEAD";

  if (lesend) return leseAbfangen<T>(p);

  if (LESENDE_POSTS.some((r) => r.test(p))) return null;
  if (STILL_ABGEWIESEN.some((r) => r.test(p))) return { art: "absage", text: absageText() };

  const t = (body ?? {}) as Record<string, unknown>;

  // --- Konto: Name, Profilbild, Sprache --------------------------------
  // Der Server weist das mit 403 ab (DEMO_READONLY), und das ist richtig so.
  // Ohne Abfang saehe die Besucherin hier aber eine Fehlermeldung, waehrend
  // ihr sonst alles zu gelingen scheint — dieselbe Handlung, zwei Wahrheiten.
  // Also verhaelt es sich wie der Rest: es wirkt, bleibt im Browser, und ein
  // Neuladen stellt den Ausgangsstand her.
  if (p === "/auth/account" && (method === "POST" || method === "PATCH")) {
    for (const k of ["username", "displayName", "email", "photoUrl", "language"]) {
      if (t[k] !== undefined) S.konto[k] = t[k];
    }
    signal();
    rueckmeldung();
    return { art: "antwort", wert: { user: { ...(S.gesehenKonto ?? {}), ...S.konto } } as T };
  }

  // --- Objekt zeichnen -------------------------------------------------
  let g = teile(P.featureNeu, p);
  if (g && method === "POST") {
    const layerId = g[0] as string;
    const featureId = naechsteKennung("f");
    const geometry = (t.geometry ?? { type: "Point", coordinates: [0, 0] }) as DemoGeometrie;
    const values = (t.values ?? {}) as Record<string, unknown>;
    S.objekte.set(key(layerId, featureId), { layerId, featureId, geometry, values });
    signal();
    rueckmeldung();
    return { art: "antwort", wert: { ok: true, featureId } as T };
  }

  // --- Sachdaten im Steckbrief ----------------------------------------
  g = teile(P.feature, p);
  if (g && method === "PATCH") {
    const k = key(g[0] as string, g[1] as string);
    const neu = ((t.values ?? {}) as Record<string, unknown>);
    S.werte.set(k, { ...(S.werte.get(k) ?? {}), ...neu });
    signal();
    rueckmeldung();
    return { art: "antwort", wert: { ok: true, updated: 1 } as T };
  }

  // --- Objekt loeschen -------------------------------------------------
  // Heute ohne Aufrufer: die Karte kennt drei Werkzeuge (waehlen, erfassen,
  // messen) und das Backend keine DELETE-Route unter /features. Steht hier
  // trotzdem, weil das SELBST gezeichnete Objekt das einzige ist, das sich in
  // der Demo ueberhaupt sauber zuruecknehmen laesst — es lebt ja nur hier.
  // Fuer ein Serverobjekt bleibt es bei der Absage: aus der Liste verschwaende
  // es, auf der Kachel bliebe es stehen, und dieser halbe Stand saehe nicht
  // nach Demo aus, sondern nach Fehler.
  if (g && method === "DELETE") {
    const layerId = g[0] as string;
    const featureId = g[1] as string;
    if (!demoIstDemoObjekt(featureId)) return absage<T>();
    const k = key(layerId, featureId);
    S.objekte.delete(k);
    S.werte.delete(k);
    S.maengel.delete(k);
    for (const a of S.anhaenge.get(k) ?? []) entferneEigenenAnhang(a.id);
    S.anhaenge.delete(k);
    signal();
    rueckmeldung();
    return { art: "antwort", wert: { ok: true } as T };
  }

  // --- Aenderung zuruecknehmen ----------------------------------------
  g = teile(P.revert, p);
  if (g && method === "POST") {
    // Der Verlauf bleibt serverwahr (eine erfundene Historie waere die
    // unehrlichste aller Faelschungen). Zurueckgenommen werden kann deshalb
    // nur, was die Demo selbst ueberlagert hat: die Spalte wieder freigeben,
    // dann steht der Serverwert wieder da.
    const k = key(g[0] as string, g[1] as string);
    const spalte = typeof t.column === "string" ? t.column : null;
    const stand = S.werte.get(k);
    if (stand && spalte) {
      delete stand[spalte];
      if (Object.keys(stand).length === 0) S.werte.delete(k);
    } else if (stand && !spalte) {
      S.werte.delete(k);
    }
    signal();
    rueckmeldung();
    return { art: "antwort", wert: { ok: true } as T };
  }

  // --- Spielgeraete (Baustein Geraeteverwaltung) ------------------------
  // Wie bei den Maengeln: anlegen, aendern und loeschen wirken, bleiben im
  // Browser, und ein Neuladen stellt den Ausgangsstand her. Bis 15.09.2026
  // endete jeder dieser Handgriffe in der Absage — der neue Baustein war in
  // der Demo nur anzusehen.
  g = teile(P.geraete, p);
  if (g && method === "POST") {
    const neu = neuesGeraet(g[0] as string, g[1] as string, t);
    const k = key(neu.layerId, neu.featureId);
    S.geraete.set(k, [...(S.geraete.get(k) ?? []), neu]);
    rueckmeldung();
    return { art: "antwort", wert: neu as T };
  }
  g = teile(P.geraet, p);
  if (g && (method === "PATCH" || method === "DELETE")) {
    const id = g[0] as string;
    if (method === "DELETE") {
      S.weg.add(id);
      for (const [k, liste] of S.geraete) S.geraete.set(k, liste.filter((x) => x.id !== id));
      rueckmeldung();
      return { art: "antwort", wert: { ok: true } as T };
    }
    return { art: "antwort", wert: geraetFortschreiben(id, t) as T };
  }

  // --- Spielplatzpruefung ----------------------------------------------
  // Das Protokoll ist der laengste Weg im ganzen Baustein: beginnen, Punkt fuer
  // Punkt bewerten, abschliessen. In der Demo lief er bis 17.09.2026 in die
  // Absage — wer sich die Pruefung ansehen wollte, sah die Absage. Jetzt laeuft
  // er im Browser durch, mit denselben Regeln: unbewertete Punkte verhindern
  // den Abschluss (das prueft die Oberflaeche), das fertige Protokoll steht in
  // der Kontrollhistorie, und ein Neuladen stellt den Ausgangsstand her.
  g = teile(P.pruefungen, p);
  if (g && method === "POST") {
    const layerId = g[0] as string;
    const featureId = g[1] as string;
    const art = (t.art as ApiPruefung["art"]) ?? "haupt";
    const heute = new Date().toISOString().slice(0, 10);
    const geraete = S.geraete.get(key(layerId, featureId)) ?? [];
    const punkte: ApiPruefPunkt[] = [
      { key: "platz:sicht", typ: "platz", titel: "Sichtprüfung der Fläche", kategorie: "Sauberkeit", ergebnis: null },
      { key: "platz:fallschutz", typ: "platz", titel: "Fallschutz", kategorie: "Fallschutz", ergebnis: null },
      ...geraete.map((x) => ({ key: `geraet:${x.id}`, typ: "geraet" as const, titel: x.bezeichnung || artName(x.art, true), geraetId: x.id, ergebnis: null })),
    ];
    const neu: ApiPruefung = {
      id: `demo-pruefung-${Date.now()}`,
      layerId,
      featureId,
      art,
      artName: art,
      datum: (t.datum as string) || heute,
      pruefer: (t.pruefer as string) || "Demo",
      firma: (t.firma as string) || "",
      ergebnis: null,
      bemerkung: "",
      punkte,
      basisPruefungId: null,
      quelle: "protokoll",
      abgeschlossenAt: null,
      abgeschlossenVon: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      maengel: 0,
      vorschlag: "ohne_befund",
    };
    const k = key(layerId, featureId);
    S.pruefungen.set(k, [...(S.pruefungen.get(k) ?? []), neu]);
    rueckmeldung();
    return { art: "antwort", wert: neu as T };
  }
  g = teile(P.pruefungAbschluss, p);
  if (g && method === "POST") {
    const id = g[0] as string;
    const jetzt = new Date().toISOString();
    // Sammelbecken statt einer Variablen im Abschluss: TypeScript verfolgt
    // Zuweisungen in einem Rueckruf nicht und haelt sie danach fuer unmoeglich.
    const fertige: ApiPruefung[] = [];
    for (const [k, liste] of S.pruefungen) {
      S.pruefungen.set(
        k,
        liste.map((x) => {
          if (x.id !== id) return x;
          const befunde = x.punkte.filter((y) => y.ergebnis === "mangel").length;
          const neu: ApiPruefung = {
            ...x,
            ergebnis: (t.ergebnis as ApiPruefung["ergebnis"]) ?? (befunde ? "maengel" : "ohne_befund"),
            abgeschlossenAt: jetzt,
            abgeschlossenVon: "Demo",
            maengel: befunde,
            updatedAt: jetzt,
          };
          fertige.push(neu);
          return neu;
        }),
      );
    }
    rueckmeldung();
    const fertig = fertige[0];
    return { art: "antwort", wert: (fertig ? { ...fertig, angelegteMaengel: fertig.maengel, behobeneMaengel: 0 } : { ok: true }) as T };
  }
  g = teile(P.pruefung, p);
  if (g && (method === "PATCH" || method === "DELETE")) {
    const id = g[0] as string;
    if (method === "DELETE") {
      for (const [k, liste] of S.pruefungen) S.pruefungen.set(k, liste.filter((x) => x.id !== id));
      rueckmeldung();
      return { art: "antwort", wert: { ok: true } as T };
    }
    const fortgeschriebene: ApiPruefung[] = [];
    for (const [k, liste] of S.pruefungen) {
      S.pruefungen.set(
        k,
        liste.map((x) => {
          if (x.id !== id) return x;
          const neu: ApiPruefung = { ...x, ...(t as Partial<ApiPruefung>), updatedAt: new Date().toISOString() };
          fortgeschriebene.push(neu);
          return neu;
        }),
      );
    }
    rueckmeldung();
    return { art: "antwort", wert: (fortgeschriebene[0] ?? { ok: true }) as T };
  }

  // --- Kategorienfarben der Diagramme ----------------------------------
  // Darstellung, kein Datenbestand — in der Demo darf man sie ausprobieren.
  g = teile(P.farben, p);
  if (g && method === "PUT") {
    S.farben.set(g[0] as string, { ...(t as Record<string, string>) });
    return { art: "antwort", wert: { categoryColors: t } as T };
  }

  // --- Erinnerung an den Handlungsbedarf --------------------------------
  // Der Schalter steht in der Demo mit, die Mail geht selbstverstaendlich nicht
  // raus: es gibt keine Adresse, an die sie gehoerte.
  g = teile(P.erinnerung, p);
  if (g && method === "PUT") {
    S.erinnerung.set(g[0] as string, { aktiv: !!t.aktiv, rhythmus: (t.rhythmus as "taeglich" | "woechentlich") ?? "taeglich" });
    rueckmeldung();
    return {
      art: "antwort",
      wert: { aktiv: !!t.aktiv, rhythmus: (t.rhythmus as string) ?? "taeglich", email: "demo@beispiel.de", letzteAt: null, mailBereit: true } as T,
    };
  }

  // --- Mangel anlegen --------------------------------------------------
  g = teile(P.maengel, p);
  if (g && method === "POST") {
    const layerId = g[0] as string;
    const featureId = g[1] as string;
    const m = neuerMangel(layerId, featureId, alsMangelfelder(t));
    const k = key(layerId, featureId);
    S.maengel.set(k, [...(S.maengel.get(k) ?? []), m]);
    rueckmeldung();
    return { art: "antwort", wert: m as T };
  }

  // --- Mangel bearbeiten / loeschen ------------------------------------
  g = teile(P.mangel, p);
  if (g && (method === "PATCH" || method === "DELETE")) {
    const id = g[0] as string;
    if (method === "DELETE") {
      S.weg.add(id);
      entferneEigenenMangel(id);
      rueckmeldung();
      return { art: "antwort", wert: { ok: true } as T };
    }
    return { art: "antwort", wert: mangelFortschreiben(id, alsMangelfelder(t)) as T };
  }

  // --- Foto/Anhang: Stern und Loeschen ---------------------------------
  g = teile(P.anhang, p);
  if (g && (method === "PATCH" || method === "DELETE")) {
    const id = g[0] as string;
    if (method === "DELETE") {
      S.weg.add(id);
      entferneEigenenAnhang(id);
      rueckmeldung();
      return { art: "antwort", wert: { ok: true } as T };
    }
    return { art: "antwort", wert: anhangFortschreiben(id, t) as T };
  }

  // --- Flaeche teilen, Datei-Import: bewusst NICHT nachgestellt ---------
  // Beide brauchen Geometrie, die nur der Server hat: ST_Split rechnet auf der
  // Originalflaeche (die im Browser nirgends liegt), und der Import liest eine
  // Geodatei mit GDAL. Erfundene Teilstuecke oder erfundene Spaltennamen aus
  // der eigenen Datei saehen fuer den Betrachter nicht wie eine Demo aus,
  // sondern wie ein Fehler. Lieber eine ehrliche Absage.
  if (teile(P.split, p) || teile(P.importLauf, p)) return absage<T>();

  // --- Alles Uebrige ---------------------------------------------------
  // Default-deny, spiegelbildlich zum Backend-Riegel: Diagramme, Exporte,
  // Layer-Konfiguration, Kontakt, Benutzer. Es verlaesst kein Nicht-GET den
  // Browser, den die Simulation nicht ausdruecklich kennt.
  return absage<T>();
}

/**
 * Lesend — hier ist nur EIN Fall zu behandeln: ein in dieser Sitzung
 * erfundenes Objekt kennt der Server nicht, sein GET liefe in einen 404.
 * Alles andere geht raus und wird hinterher ueberlagert (siehe demoUeberlagern).
 */
function leseAbfangen<T>(p: string): DemoErgebnis<T> | null {
  let g = teile(P.feature, p);
  if (g && demoIstDemoObjekt(g[1] as string)) {
    const k = key(g[0] as string, g[1] as string);
    const o = S.objekte.get(k);
    if (!o) return null;
    return { art: "antwort", wert: { layerId: o.layerId, featureId: o.featureId, values: { id: o.featureId.toUpperCase(), ...o.values, ...(S.werte.get(k) ?? {}) } } as T };
  }
  g = teile(P.anhaenge, p);
  if (g && demoIstDemoObjekt(g[1] as string)) return { art: "antwort", wert: (S.anhaenge.get(key(g[0] as string, g[1] as string)) ?? []) as T };
  g = teile(P.maengel, p);
  if (g && demoIstDemoObjekt(g[1] as string)) return { art: "antwort", wert: (S.maengel.get(key(g[0] as string, g[1] as string)) ?? []) as T };
  g = teile(P.geraete, p);
  if (g && demoIstDemoObjekt(g[1] as string)) return { art: "antwort", wert: (S.geraete.get(key(g[0] as string, g[1] as string)) ?? []) as T };
  g = teile(P.verlauf, p);
  if (g && demoIstDemoObjekt(g[1] as string)) return { art: "antwort", wert: [] as T };
  g = teile(P.schnitt, p);
  if (g && demoIstDemoObjekt(g[1] as string)) return { art: "antwort", wert: [] as T };
  return null;
}

/** Datei-Uploads (multipart). Nur der Anhang wird nachgestellt. */
export function demoAbfangenForm<T>(path: string, form: FormData): DemoErgebnis<T> | null {
  if (!S.an) return null;
  const p = ohneAbfrage(path);
  const g = teile(P.anhaenge, p);
  if (!g) return absage<T>(); // Import-/Vorschau-Uploads: siehe Begruendung oben
  const datei = form.get("file");
  if (!(datei instanceof File)) return absage<T>();
  const bereichRoh = form.get("bereich");
  const bereich = typeof bereichRoh === "string" ? bereichRoh : null;
  const layerId = g[0] as string;
  const featureId = g[1] as string;
  const id = naechsteKennung("a");
  // Die Bytes liegen ab hier NUR hier. photoUrl() fragt vor dem Serverpfad
  // danach; freigegeben wird die Adresse beim Loeschen und beim Zuruecksetzen.
  S.bilder.set(id, URL.createObjectURL(datei));
  const a: ApiAttachment = {
    id,
    layerId,
    featureId,
    kind: artZuKind(datei.type, datei.name),
    filename: datei.name,
    mime: datei.type || "application/octet-stream",
    size: datei.size,
    // takenAt aus dem Dateidatum statt aus EXIF: die Oberflaeche schneidet mit
    // .slice(0,10) und .slice(0,4), also muss es ein ISO-Zeitstempel sein.
    // Eine Bildunterschrift entsteht serverseitig aus dem XMP-Block — die
    // bleibt in der Demo leer (siehe Rueckgabetext).
    meta: { takenAt: new Date(datei.lastModified || Date.now()).toISOString(), ...(bereich ? { bereich } : {}) },
    createdAt: jetzt(),
  };
  const k = key(layerId, featureId);
  S.anhaenge.set(k, [...(S.anhaenge.get(k) ?? []), a]);
  rueckmeldung();
  return { art: "antwort", wert: a as T };
}

function artZuKind(mime: string, name: string): ApiAttachment["kind"] {
  if (mime.startsWith("image/")) return "photo";
  if (mime === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
  if (/sheet|excel/i.test(mime) || /\.xlsx?$/i.test(name)) return "xlsx";
  return "other";
}

// ------------------------------------------------------------
// Fortschreiben einzelner Zeilen
// ------------------------------------------------------------

const SCHWEREN: ReadonlyArray<ApiMangel["severity"]> = ["gering", "mittel", "hoch", "gefahr"];
const ZUSTAENDE: ReadonlyArray<ApiMangel["status"]> = ["offen", "in_arbeit", "behoben", "abgelehnt"];

/**
 * Aus einem rohen Anfragekoerper die Mangelfelder herausklauben — Feld fuer
 * Feld statt mit einer Umdeutung. Der Koerper kommt aus der Oberflaeche und ist
 * fuer die Simulation genauso Fremdeingabe wie fuer den Server; ein `as
 * Partial<ApiMangel>` wuerde nur behaupten, was hier geprueft wird.
 */
function alsMangelfelder(b: Record<string, unknown>): Partial<ApiMangel> {
  const f: Partial<ApiMangel> = {};
  if (typeof b.category === "string") f.category = b.category;
  if (typeof b.inspector === "string") f.inspector = b.inspector;
  if (typeof b.description === "string") f.description = b.description;
  if (typeof b.source === "string") f.source = b.source;
  if (typeof b.foundAt === "string" || b.foundAt === null) f.foundAt = b.foundAt;
  if (typeof b.fixedAt === "string" || b.fixedAt === null) f.fixedAt = b.fixedAt;
  const schwere = SCHWEREN.find((s) => s === b.severity);
  if (schwere) f.severity = schwere;
  const zustand = ZUSTAENDE.find((s) => s === b.status);
  if (zustand) f.status = zustand;
  // massnahme und frist kamen NACH dieser Liste dazu und fehlten hier: die
  // Demo meldete „Gespeichert" und verwarf sie still. Wer die Frist eines
  // Mangels umstellte, sah weder in der Liste noch auf der Karte etwas.
  if (typeof b.massnahme === "string") f.massnahme = b.massnahme;
  const frist = FRISTEN.find((x) => x === b.frist);
  if (frist) f.frist = frist;
  if (Array.isArray(b.attachmentIds)) f.attachmentIds = (b.attachmentIds as unknown[]).map(String);
  // Geraetezuordnung (Baustein Geraeteverwaltung) — fehlte hier ebenso und
  // wurde in der Demo still verworfen.
  if (typeof b.geraetId === "string" || b.geraetId === null) f.geraetId = b.geraetId;
  return f;
}

function neuerMangel(layerId: string, featureId: string, b: Partial<ApiMangel>): ApiMangel {
  return {
    id: naechsteKennung("m"),
    layerId,
    featureId,
    category: b.category ?? "",
    inspector: b.inspector ?? "",
    description: b.description ?? "",
    massnahme: b.massnahme ?? "",
    severity: b.severity ?? "gering",
    status: b.status ?? "offen",
    foundAt: b.foundAt ?? heute(),
    fixedAt: b.fixedAt ?? null,
    source: "demo",
    attachmentIds: b.attachmentIds ?? [],
    geraetId: b.geraetId ?? null,
    createdAt: jetzt(),
    updatedAt: jetzt(),
  };
}

/** Servertreue, die man nachbilden muss: `behoben` setzt fixedAt, wenn es leer war. */
function mitStatusregel(m: ApiMangel, b: Partial<ApiMangel>): ApiMangel {
  const n: ApiMangel = { ...m, ...b, updatedAt: jetzt() };
  if (n.status === "behoben" && !n.fixedAt) n.fixedAt = heute();
  return n;
}

function mangelFortschreiben(id: string, b: Partial<ApiMangel>): ApiMangel {
  for (const [k, liste] of S.maengel) {
    const i = liste.findIndex((x) => x.id === id);
    if (i < 0) continue;
    const neu = mitStatusregel(liste[i] as ApiMangel, b);
    const kopie = liste.slice();
    kopie[i] = neu;
    S.maengel.set(k, kopie);
    rueckmeldung();
    return neu;
  }
  // Echte Zeile: nur die Aenderung merken. Zurueckgegeben wird die zuletzt vom
  // Server gesehene Zeile mit der Aenderung darauf — erfinden muessten wir
  // sonst Felder, die die Oberflaeche gleich wieder anzeigt.
  const vorher = S.mangelPatch.get(id) ?? {};
  const zusammen = { ...vorher, ...b };
  const basis = S.gesehenMangel.get(id);
  const neu = basis ? mitStatusregel(basis, zusammen) : ({ ...leererMangel(id), ...zusammen } as ApiMangel);
  S.mangelPatch.set(id, { ...zusammen, updatedAt: neu.updatedAt, fixedAt: neu.fixedAt });
  rueckmeldung();
  return neu;
}

function leererMangel(id: string): ApiMangel {
  return { id, layerId: "", featureId: "", category: "", inspector: "", description: "", massnahme: "", severity: "gering", status: "offen", foundAt: null, fixedAt: null, source: null, attachmentIds: [], createdAt: jetzt(), updatedAt: jetzt() };
}

function anhangFortschreiben(id: string, b: Record<string, unknown>): ApiAttachment {
  // Der einzige schreibbare Schluessel ist `starred`, und er liegt in `meta`.
  const patch: Partial<ApiAttachment> = "starred" in b ? { meta: { starred: !!b.starred } } : {};
  for (const [k, liste] of S.anhaenge) {
    const i = liste.findIndex((x) => x.id === id);
    if (i < 0) continue;
    const alt = liste[i] as ApiAttachment;
    const neu: ApiAttachment = { ...alt, meta: { ...alt.meta, ...(patch.meta ?? {}) } };
    const kopie = liste.slice();
    kopie[i] = neu;
    S.anhaenge.set(k, kopie);
    rueckmeldung();
    return neu;
  }
  const vorher = S.anhangPatch.get(id) ?? {};
  const meta = { ...(vorher.meta ?? {}), ...(patch.meta ?? {}) };
  S.anhangPatch.set(id, { meta });
  const basis = S.gesehenAnhang.get(id);
  rueckmeldung();
  return basis
    ? { ...basis, meta: { ...basis.meta, ...meta } }
    : { id, layerId: "", featureId: "", kind: "photo", filename: "", mime: "", size: 0, meta, createdAt: jetzt() };
}

function entferneEigenenAnhang(id: string): void {
  const url = S.bilder.get(id);
  if (url) {
    URL.revokeObjectURL(url);
    S.bilder.delete(id);
  }
  for (const [k, liste] of S.anhaenge) {
    if (!liste.some((x) => x.id === id)) continue;
    S.anhaenge.set(k, liste.filter((x) => x.id !== id));
  }
}

const GERAET_FELDER = ["art", "bezeichnung", "inventarnummer", "hersteller", "modell", "aufbaufirma", "baujahr", "zustand", "fallschutz", "norm", "anzahl", "notiz", "abgebautAm", "garantieBis", "nutzungsdauerJahre", "ersatzkosten", "fotoIds"] as const;

function alsGeraetefelder(b: Record<string, unknown>): Partial<ApiGeraet> {
  const f: Record<string, unknown> = {};
  for (const k of GERAET_FELDER) if (b[k] !== undefined) f[k] = b[k];
  return f as Partial<ApiGeraet>;
}

/** Abgeleitete Felder wie der Server: Name der Art und Alter aus dem Baujahr. */
function mitAbleitung(x: ApiGeraet): ApiGeraet {
  return { ...x, artName: artName(x.art), alter: x.baujahr == null ? null : Math.max(0, new Date().getFullYear() - x.baujahr) };
}

function neuesGeraet(layerId: string, featureId: string, b: Record<string, unknown>): ApiGeraet {
  return mitAbleitung({
    id: naechsteKennung("g"),
    layerId,
    featureId,
    art: "sonstiges",
    artName: "",
    bezeichnung: "",
    inventarnummer: "",
    hersteller: "",
    modell: "",
    baujahr: null,
    alter: null,
    zustand: "unbekannt",
    fallschutz: "",
    norm: "",
    anzahl: 1,
    notiz: "",
    abgebautAm: null,
    offeneMaengel: 0,
    maengel: 0,
    createdAt: jetzt(),
    updatedAt: jetzt(),
    ...alsGeraetefelder(b),
  });
}

function geraetFortschreiben(id: string, b: Record<string, unknown>): ApiGeraet {
  const felder = alsGeraetefelder(b);
  for (const [k, liste] of S.geraete) {
    const i = liste.findIndex((x) => x.id === id);
    if (i < 0) continue;
    const neu = mitAbleitung({ ...(liste[i] as ApiGeraet), ...felder, updatedAt: jetzt() });
    const kopie = liste.slice();
    kopie[i] = neu;
    S.geraete.set(k, kopie);
    rueckmeldung();
    return neu;
  }
  // Echte Zeile: nur die Aenderung merken, zurueck geht die zuletzt gesehene Zeile mit ihr darauf
  const zusammen = { ...(S.geraetPatch.get(id) ?? {}), ...felder };
  S.geraetPatch.set(id, zusammen);
  const basis = S.gesehenGeraet.get(id);
  rueckmeldung();
  return mitAbleitung({ ...(basis ?? neuesGeraet("", "", {})), id, ...zusammen, updatedAt: jetzt() });
}

function entferneEigenenMangel(id: string): void {
  for (const [k, liste] of S.maengel) {
    if (!liste.some((x) => x.id === id)) continue;
    S.maengel.set(k, liste.filter((x) => x.id !== id));
  }
}

// ------------------------------------------------------------
// Ueberlagern: die Serverantwort um das Gemerkte ergaenzen
// ------------------------------------------------------------

/**
 * Wird an BEIDEN Ruecksprungpunkten von `req()` gerufen — nach dem
 * 304-Kurzschluss und nach `parseJson`. Der ETag-Speicher selbst bleibt dabei
 * aussen vor: dort liegt ausschliesslich Serverwahrheit, sonst faelschte sich
 * die Demo in einen Speicher hinein, der einen echten 304 ueberlebt.
 *
 * Nichts wird an Ort und Stelle geaendert; es entstehen neue Arrays/Objekte.
 */
export function demoUeberlagern<T>(method: string, path: string, body: unknown, daten: T): T {
  if (!S.an) return daten;
  const p = ohneAbfrage(path);

  // Kontoangaben: den zuletzt echten Stand merken (damit ein PATCH die VOLLE
  // Zeile zurueckgeben kann) und die Sitzungsaenderungen darueberlegen. Ohne
  // das Zweite fiele ein geaenderter Name beim naechsten /auth/me still zurueck.
  if (p === "/auth/me" && method === "GET") {
    const d = daten as { user?: Record<string, unknown> } | null;
    if (d?.user) {
      S.gesehenKonto = { ...d.user };
      if (Object.keys(S.konto).length) return { ...d, user: { ...d.user, ...S.konto } } as T;
    }
    return daten;
  }

  let g = teile(P.projekt, p);
  if (g && method === "GET" && daten && typeof daten === "object") {
    const farben = S.farben.get(g[0] as string);
    return (farben ? { ...(daten as object), categoryColors: farben } : daten) as T;
  }

  g = teile(P.feature, p);
  if (g && method === "GET") {
    const zusatz = S.werte.get(key(g[0] as string, g[1] as string));
    if (!zusatz) return daten;
    const d = daten as { layerId: string; featureId: string; values: Record<string, unknown> };
    return { ...d, values: { ...d.values, ...zusatz } } as T;
  }

  g = teile(P.anhaenge, p);
  if (g && method === "GET" && Array.isArray(daten)) {
    const echte = (daten as ApiAttachment[]).filter((a) => !S.weg.has(a.id));
    for (const a of echte) S.gesehenAnhang.set(a.id, a);
    const gemischt = echte.map((a) => {
      const patch = S.anhangPatch.get(a.id);
      return patch ? { ...a, meta: { ...a.meta, ...(patch.meta ?? {}) } } : a;
    });
    return [...gemischt, ...(S.anhaenge.get(key(g[0] as string, g[1] as string)) ?? [])] as T;
  }

  g = teile(P.maengel, p);
  if (g && method === "GET" && Array.isArray(daten)) {
    const echte = (daten as ApiMangel[]).filter((m) => !S.weg.has(m.id));
    for (const m of echte) S.gesehenMangel.set(m.id, m);
    const gemischt = echte.map((m) => {
      const patch = S.mangelPatch.get(m.id);
      return patch ? { ...m, ...patch } : m;
    });
    return [...gemischt, ...(S.maengel.get(key(g[0] as string, g[1] as string)) ?? [])] as T;
  }

  g = teile(P.pruefungen, p);
  if (g && method === "GET" && daten && typeof daten === "object") {
    const k = key(g[0] as string, g[1] as string);
    const eigene = S.pruefungen.get(k) ?? [];
    if (!eigene.length) return daten;
    const d = daten as { pruefungen?: ApiPruefung[] };
    return { ...d, pruefungen: [...eigene, ...(d.pruefungen ?? [])] } as T;
  }

  g = teile(P.geraete, p);
  if (g && method === "GET" && Array.isArray(daten)) {
    const echte = (daten as ApiGeraet[]).filter((x) => !S.weg.has(x.id));
    for (const x of echte) S.gesehenGeraet.set(x.id, x);
    const gemischt = echte.map((x) => {
      const patch = S.geraetPatch.get(x.id);
      return patch ? mitAbleitung({ ...x, ...patch }) : x;
    });
    return [...gemischt, ...(S.geraete.get(key(g[0] as string, g[1] as string)) ?? [])] as T;
  }

  g = teile(P.queryList, p);
  if (g && Array.isArray(daten)) return listeUeberlagern(g[0] as string, body, daten as Objektzeile[]) as T;

  g = teile(P.queryCount, p);
  if (g && daten && typeof daten === "object") {
    const eigene = zaehleEigene(g[0] as string);
    if (!eigene) return daten;
    const d = daten as unknown as { matched: number; total: number };
    return { matched: d.matched + eigene, total: d.total + eigene } as unknown as T;
  }

  g = teile(P.hinweise, p);
  if (g && method === "GET" && daten && typeof daten === "object") {
    return hinweiseUeberlagern(daten as unknown as HinweisCollection) as unknown as T;
  }

  return daten;
}

/**
 * Die Ausrufezeichen der Karte, um die Aenderungen dieser Demo-Sitzung korrigiert.
 *
 * ANLASS (Befund 15.09.2026): in der Demo geht ein Statuswechsel NICHT an den
 * Server — er landet nur in dieser Simulation. Die Hinweisschicht fragte aber
 * `/projects/:id/hinweise` beim Server, und der kennt die Aenderung nicht. Wer
 * einen Gefahr-Mangel auf „behoben" setzte, sah das rote Zeichen stehen
 * bleiben. Die Invalidierung im GfiPanel machte es sogar sichtbarer: sie holte
 * den unveraenderten Serverstand prompt zurueck.
 *
 * Gerechnet wird mit DENSELBEN Regeln wie in backend/src/hinweise/
 * hinweise.controller.ts — Gefahr zaehlt bei Status offen/in_arbeit, dazu
 * ueberschrittene Fristen (kurzfristig, drei_monate; „sofort" steckt schon in
 * der Gefahr). Weicht das Backend dort ab, muss es hier nachgezogen werden.
 *
 * EHRLICHE GRENZE: eine Flaeche, die beim Server GAR KEIN Zeichen hatte und in
 * dieser Sitzung eines bekommen muesste, kann keines bekommen — die Lage des
 * Zeichens kommt ausschliesslich vom Server, und fuer eine nie gemeldete
 * Flaeche liegt sie nicht vor. Umgekehrt (Zeichen weg) und hin und zurueck
 * (weg, dann wieder da) stimmt, und das ist der Weg, den eine Demo geht.
 */
function hinweiseUeberlagern(daten: HinweisCollection): HinweisCollection {
  // Was der Server sagt, als Vorlage merken — nur wegen der Geometrie.
  for (const f of daten.features ?? []) {
    S.gesehenHinweis.set(key(f.properties.layerId, f.properties.featureId), f);
  }

  // Welche Flaechen hat diese Sitzung ueberhaupt angefasst? Nur fuer die duerfen
  // wir rechnen — bei allen anderen kennen wir die Maengel nicht vollstaendig
  // und wuerden aus Unwissen ein Zeichen loeschen.
  const beruehrt = new Set<string>();
  for (const m of S.gesehenMangel.values()) beruehrt.add(key(m.layerId, m.featureId));
  for (const [k, liste] of S.maengel) if (liste.length) beruehrt.add(k);
  if (!beruehrt.size) return daten;

  const heute = new Date();
  const uebrig = (daten.features ?? []).filter((f) => !beruehrt.has(key(f.properties.layerId, f.properties.featureId)));

  const neue: HinweisCollection["features"] = [];
  for (const k of beruehrt) {
    const [layerId, featureId] = [k.slice(0, k.indexOf("/")), k.slice(k.indexOf("/") + 1)];
    // Wirksamer Stand: echte Zeilen mit ihren Aenderungen, plus selbst erfasste,
    // abzueglich geloeschter.
    const echte = [...S.gesehenMangel.values()]
      .filter((m) => m.layerId === layerId && m.featureId === featureId && !S.weg.has(m.id))
      .map((m) => ({ ...m, ...(S.mangelPatch.get(m.id) ?? {}) }));
    const eigene = (S.maengel.get(k) ?? []).filter((m) => !S.weg.has(m.id));
    const alle = [...echte, ...eigene];

    const offen = (m: ApiMangel) => m.status === "offen" || m.status === "in_arbeit";
    const gefahr = alle.filter((m) => m.severity === "gefahr" && offen(m)).length;
    const ueberfaellig = alle.filter((m) => {
      if (!offen(m)) return false;
      const f = fristVon(m);
      if (f !== "kurzfristig" && f !== "drei_monate") return false;
      return fristUeberschritten(zuBehebenBis(f, m.foundAt ?? m.createdAt), m.status, heute);
    }).length;

    // Gruende aus Meldungen bleiben, wie der Server sie sah — die rechnen wir
    // hier nicht nach; geaendert hat die Sitzung nur Maengel.
    const vorlage = S.gesehenHinweis.get(k);
    const fremd = (vorlage?.properties.gruende ?? []).filter(
      (x) => x.art !== "mangel_gefahr" && x.art !== "frist_ueberschritten",
    );
    const gruende: HinweisGrund[] = [...fremd];
    if (gefahr) gruende.unshift({ art: "mangel_gefahr", anzahl: gefahr });
    if (ueberfaellig) gruende.push({ art: "frist_ueberschritten", anzahl: ueberfaellig });
    if (!gruende.length) continue;                       // nichts mehr zu tun -> kein Zeichen
    if (!vorlage?.geometry) continue;                    // ohne Lage kein Zeichen (siehe oben)

    const warnung = gruende.some(
      (x) => x.art === "mangel_gefahr" || x.art === "frist_ueberschritten" || x.art === "meldung_akut",
    );
    neue.push({
      ...vorlage,
      properties: {
        ...vorlage.properties,
        stufe: warnung ? "warnung" : "hinweis",
        anzahl: gruende.reduce((n, x) => n + x.anzahl, 0),
        gruende,
      },
    });
  }
  return { type: "FeatureCollection", features: [...uebrig, ...neue] };
}

function zaehleEigene(layerId: string): number {
  let n = 0;
  for (const o of S.objekte.values()) if (o.layerId === layerId) n++;
  return n;
}

/**
 * Die Grenze dieser Simulation verlaeuft zwischen AUFZAEHLEN und AGGREGIEREN.
 * Aufzaehlen ist billig und ehrlich: die eigenen Zeilen dazulegen. Nachrechnen
 * waere halbgar — deshalb bleiben Diagramme, Kennzahlen und die
 * Maengel-Statistik unangetastet (sie rechnen serverseitig mit Filtern,
 * Ortsteil-Verschnitten und Bevoelkerungsbezug).
 *
 * Filter werden hier bewusst NICHT nachgewertet: ein selbst gezeichnetes Objekt
 * steht auch dann in der Liste, wenn ein Filter aktiv ist.
 */
function listeUeberlagern(layerId: string, body: unknown, zeilen: Objektzeile[]): Objektzeile[] {
  lerneKopffelder(layerId, zeilen);
  const mitWerten = zeilen.map((z) => {
    const zusatz = S.werte.get(key(layerId, z.fid));
    return zusatz ? kopfNachziehen(layerId, { ...z, values: { ...(z.values ?? {}), ...zusatz } }, zusatz) : z;
  });

  const b = (body ?? {}) as { limit?: unknown; offset?: unknown; clauses?: unknown };
  const offset = typeof b.offset === "number" ? b.offset : 0;
  if (offset > 0) return mitWerten; // eigene Zeilen stehen nur auf der ersten Seite
  const eigene = eigeneZeilen(layerId, mitWerten);
  if (!eigene.length) return mitWerten;

  // Nennt die Abfrage ausdruecklich Kennungen (Zoom auf EIN Objekt, Atlas-
  // Auswahl), dann muss die Antwort punktgenau bleiben: hier darf nur stehen,
  // was auch gefragt war.
  const genannt = genannteKennungen(b.clauses);
  if (genannt) return [...eigene.filter((z) => genannt.has(z.fid)), ...mitWerten];

  const limit = typeof b.limit === "number" ? b.limit : null;
  if (limit == null) return [...eigene, ...mitWerten];
  // Eine kleine, volle Seite ist ein Nachschlagen und keine Liste — dort waere
  // das Verdraengen einer Serverzeile ein echter Fehler (der Zoom traefe das
  // falsche Objekt). Erst ab Listengroesse darf hinten etwas abfallen.
  if (mitWerten.length >= limit && limit < 20) return mitWerten;
  return [...eigene, ...mitWerten].slice(0, limit);
}

function genannteKennungen(clauses: unknown): Set<string> | null {
  if (!Array.isArray(clauses)) return null;
  const menge = new Set<string>();
  let trifft = false;
  for (const c of clauses as Array<{ op?: unknown; value?: unknown }>) {
    if (c?.op !== "=" && c?.op !== "IN") continue;
    const teilwerte = String(c.value ?? "").split(",").map((s) => s.trim());
    if (!teilwerte.some((t) => demoIstDemoObjekt(t))) continue;
    trifft = true;
    for (const t of teilwerte) menge.add(t);
  }
  return trifft ? menge : null;
}

function eigeneZeilen(layerId: string, echte: Objektzeile[]): Objektzeile[] {
  const kopf = S.kopffeld.get(layerId);
  // rows[0].total traegt die Gesamtzahl fuer das Nachladen der Objektliste —
  // steht eine eigene Zeile vorn, muss sie diese Zahl mitbringen.
  const gesamt = echte[0]?.total;
  const eigene: Objektzeile[] = [];
  for (const o of S.objekte.values()) {
    if (o.layerId !== layerId) continue;
    const werte = { ...o.values, ...(S.werte.get(key(o.layerId, o.featureId)) ?? {}) };
    eigene.push({
      fid: o.featureId,
      name: kopf?.name ? textOderNull(werte[kopf.name]) : null,
      nr: kopf?.nr ? textOderNull(werte[kopf.nr]) : null,
      district: null,
      bbox: bboxVon(o.geometry),
      values: werte,
      ...(typeof gesamt === "number" ? { total: gesamt + zaehleEigene(layerId) } : {}),
    });
  }
  return eigene;
}

function textOderNull(v: unknown): string | null {
  return v == null || v === "" ? null : String(v);
}

/**
 * Welche Spalte den Zeilenkopf fuellt, weiss nur die Ebenenkonfiguration — und
 * die kennt diese Datei bewusst nicht (sie soll von nichts abhaengen). Also
 * wird es an echten Zeilen ABGELESEN: steht in `values` genau ein Feld mit
 * demselben Text wie `name`, ist das die Namensspalte. Bleibt es mehrdeutig,
 * wird nichts gelernt und der Kopf bleibt leer — lieber blank als falsch.
 */
function lerneKopffelder(layerId: string, zeilen: Objektzeile[]): void {
  const bekannt = S.kopffeld.get(layerId);
  if (bekannt?.name && bekannt?.nr) return;
  for (const z of zeilen) {
    if (!z.values) continue;
    const treffer = { ...bekannt };
    if (!treffer.name && z.name != null) treffer.name = eindeutigeSpalte(z.values, z.name);
    if (!treffer.nr && z.nr != null) treffer.nr = eindeutigeSpalte(z.values, z.nr);
    if (treffer.name || treffer.nr) S.kopffeld.set(layerId, treffer);
    if (treffer.name && treffer.nr) return;
  }
}

function eindeutigeSpalte(values: Record<string, unknown>, text: string): string | undefined {
  let gefunden: string | undefined;
  for (const [k, v] of Object.entries(values)) {
    if (v == null || String(v) !== text) continue;
    if (gefunden) return undefined; // mehrdeutig — dann lieber gar nichts
    gefunden = k;
  }
  return gefunden;
}

/** Wird eine ueberlagerte Spalte im Zeilenkopf gezeigt, muss der Kopf mitgehen. */
function kopfNachziehen(layerId: string, z: Objektzeile, zusatz: Record<string, unknown>): Objektzeile {
  const kopf = S.kopffeld.get(layerId);
  if (!kopf) return z;
  const name = kopf.name && kopf.name in zusatz ? textOderNull(zusatz[kopf.name]) : z.name;
  const nr = kopf.nr && kopf.nr in zusatz ? textOderNull(zusatz[kopf.nr]) : z.nr;
  return { ...z, name, nr };
}

/** Umschliessendes Rechteck einer Geometrie — die Objektliste zoomt darauf. */
function bboxVon(g: DemoGeometrie): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const lauf = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    const glieder = c as unknown[];
    const x = glieder[0];
    const y = glieder[1];
    if (typeof x === "number" && typeof y === "number") {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      return;
    }
    for (const t of glieder) lauf(t);
  };
  lauf(g.coordinates);
  if (!Number.isFinite(minX)) return [0, 0, 0, 0];
  return [minX, minY, maxX, maxY];
}
